<template>
  <div>
    <HrPageLayout
      title="Manage Polls"
      subtitle="Create and manage company polls"
      icon="📊"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'HR Actions' },
        { label: 'Manage Polls' }
      ]"
    >
      <template #header-actions>
        <button class="hr-btn hr-btn-primary" @click="openCreateModal">
          <i class="bx bx-plus"></i> Create Poll
        </button>
        <button class="hr-btn hr-btn-secondary" @click="loadPolls">
          <i class="bx bx-refresh"></i> Refresh
        </button>
      </template>

      <div class="polls-management-content">
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body hr-py-3">
            <div class="polls-filters">
              <div class="hr-filter-group">
                <label class="hr-form-label">Status</label>
                <select v-model="statusFilter" @change="loadPolls" class="hr-select">
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                  <option value="Closed">Closed</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Department</label>
                <select v-model="departmentId" @change="loadPolls" class="hr-select">
                  <option :value="0">All Departments</option>
                  <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading polls...</p>
        </div>

        <div v-else class="hr-chart-card">
          <div class="hr-chart-body">
            <div v-if="!polls.length" class="hr-text-center hr-py-5 hr-text-muted">
              <i class="bx bx-pie-chart-alt" style="font-size: 48px;"></i>
              <p class="hr-mt-2">No polls found. Create your first poll.</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Visibility</th>
                    <th>Start / End</th>
                    <th>Status</th>
                    <th>Created By</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in polls" :key="p.id">
                    <td>
                      <strong>{{ p.title }}</strong>
                      <small v-if="p.description" class="hr-d-block hr-text-muted hr-text-truncate">{{ p.description }}</small>
                    </td>
                    <td><span class="hr-badge hr-badge-secondary">{{ p.visibility_type || 'company_wide' }}</span></td>
                    <td>
                      <small>{{ formatDate(p.start_date) }}</small><br>
                      <small>{{ formatDate(p.end_date) }}</small>
                    </td>
                    <td><span class="hr-badge" :class="statusBadgeClass(p.status)">{{ p.status }}</span></td>
                    <td>{{ p.created_by_name || '-' }}</td>
                    <td class="hr-text-end">
                      <div class="hr-btn-group">
                        <button @click="viewResults(p)" class="hr-btn hr-btn-sm hr-btn-primary" title="View results">
                          <i class="bx bx-bar-chart-alt"></i>
                        </button>
                        <button v-if="p.status === 'Draft'" @click="updatePollAction(p, 'publish')" class="hr-btn hr-btn-sm hr-btn-success">Publish</button>
                        <button v-if="p.status === 'Active'" @click="updatePollAction(p, 'close')" class="hr-btn hr-btn-sm hr-btn-warning">Close</button>
                        <button v-if="['Active','Closed'].includes(p.status)" @click="updatePollAction(p, 'archive')" class="hr-btn hr-btn-sm hr-btn-secondary">Archive</button>
                        <button @click="deletePollAction(p)" class="hr-btn hr-btn-sm hr-btn-danger">
                          <i class="bx bx-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <ModalWrapper ref="pollModal" :title="editPoll ? 'Edit Poll' : 'Create Poll'" size="large">
      <template #body>
        <form @submit.prevent="submitPoll" class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Title <span class="hr-text-danger">*</span></label>
            <input v-model="form.title" type="text" class="hr-input" placeholder="Poll title">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description (optional)</label>
            <textarea v-model="form.description" class="hr-textarea" rows="2" placeholder="Context for the poll"></textarea>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Start Date</label>
            <input v-model="form.startDate" type="datetime-local" class="hr-input">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">End Date <span class="hr-text-danger">*</span></label>
            <input v-model="form.endDate" type="datetime-local" class="hr-input">
          </div>
          <div class="hr-filter-group span-full">
            <label class="poll-check-label">
              <input v-model="form.allowMultiple" type="checkbox" class="hr-checkbox">
              Allow multiple answers
            </label>
          </div>
          <div class="hr-filter-group span-full">
            <label class="poll-check-label">
              <input v-model="form.showResultsBeforeClose" type="checkbox" class="hr-checkbox">
              Show results before poll closes
            </label>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Visibility</label>
            <select v-model="form.visibilityType" class="hr-select">
              <option value="company_wide">Company-wide</option>
              <option value="department">Specific departments</option>
              <option value="role">Specific roles</option>
            </select>
          </div>
          <div v-if="form.visibilityType === 'department'" class="hr-filter-group span-full">
            <label class="hr-form-label">Departments</label>
            <div class="poll-dept-list">
              <label v-for="d in departments" :key="d.id" class="poll-dept-item">
                <input v-model="form.departmentIds" type="checkbox" :value="d.id">
                {{ d.departmentName }}
              </label>
            </div>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Options <span class="hr-text-danger">*</span></label>
            <div v-for="(opt, idx) in form.options" :key="idx" class="poll-opt-row">
              <input v-model="form.options[idx]" type="text" class="hr-input" placeholder="Option text">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click="form.options.splice(idx, 1)">
                <i class="bx bx-minus"></i>
              </button>
            </div>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-outline" @click="form.options.push('')">
              <i class="bx bx-plus"></i> Add option
            </button>
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.pollModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="submitLoading" @click="submitPoll">
          <span v-if="submitLoading" class="hr-spinner small"></span>
          <i v-else class="bx bx-check"></i> {{ editPoll ? 'Update' : 'Create' }}
        </button>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="resultsModal" :title="'Poll Results: ' + resultsTitle" size="large">
      <template #body>
        <p class="hr-text-muted hr-mb-4">Total votes: {{ resultsUniqueVoters }}</p>
        <div v-for="opt in resultsOptions" :key="opt.option_id" class="hr-mb-3">
          <div class="hr-flex-between hr-mb-1">
            <span>{{ opt.option_text }}</span>
            <span>{{ opt.vote_count }} vote(s)</span>
          </div>
          <div class="poll-progress-bar">
            <div class="poll-progress-fill" :style="{ width: (resultsUniqueVoters ? (opt.vote_count / resultsUniqueVoters * 100) : 0) + '%' }"></div>
          </div>
        </div>
      </template>
    </ModalWrapper>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "PollsManagement",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      submitLoading: false,
      polls: [],
      departments: [],
      statusFilter: "",
      departmentId: 0,
      editPoll: null,
      form: {
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        allowMultiple: false,
        showResultsBeforeClose: false,
        visibilityType: "company_wide",
        departmentIds: [],
        options: ["", ""]
      },
      resultsTitle: "",
      resultsOptions: [],
      resultsUniqueVoters: 0
    };
  },
  mounted() {
    this.loadPolls();
    this.getDepartments();
    this.form.startDate = this.toDatetimeLocal(new Date());
    const end = new Date();
    end.setDate(end.getDate() + 7);
    this.form.endDate = this.toDatetimeLocal(end);
  },
  methods: {
    toDatetimeLocal(d) {
      const pad = (n) => (n < 10 ? "0" + n : n);
      return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      return isNaN(d.getTime()) ? val : d.toLocaleDateString();
    },
    statusBadgeClass(s) {
      if (s === "Active") return "hr-badge-success";
      if (s === "Draft") return "hr-badge-secondary";
      if (s === "Closed") return "hr-badge-warning";
      if (s === "Archived") return "hr-badge-dark";
      return "hr-badge-secondary";
    },
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data?.departments) {
            this.departments = res.data.data.departments;
          }
        })
        .catch(() => {});
    },
    loadPolls() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getPolls");
      data.append("sessionIdNo", sessionStorage.getItem("idNo") || "");
      data.append("sessionNames", sessionStorage.getItem("names") || "");
      if (this.statusFilter) data.append("status", this.statusFilter);
      if (this.departmentId) data.append("departmentId", this.departmentId);
      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data.success && Array.isArray(res.data.data)) {
            this.polls = res.data.data;
          } else {
            this.polls = [];
          }
        })
        .catch(() => {
          this.loading = false;
          this.polls = [];
        });
    },
    openCreateModal() {
      this.editPoll = null;
      this.form = {
        title: "",
        description: "",
        startDate: this.toDatetimeLocal(new Date()),
        endDate: this.toDatetimeLocal(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
        allowMultiple: false,
        showResultsBeforeClose: false,
        visibilityType: "company_wide",
        departmentIds: [],
        options: ["", ""]
      };
      this.$nextTick(() => {
        const modal = this.$refs.pollModal;
        if (modal && typeof modal.open === "function") {
          modal.open();
        } else {
          toast.warning("Could not open create poll dialog. Please try again.");
        }
      });
    },
    submitPoll() {
      const opts = this.form.options.filter((o) => String(o).trim());
      if (!this.form.title.trim()) {
        toast.warning("Title is required.");
        return;
      }
      if (!this.form.endDate) {
        toast.warning("End date is required.");
        return;
      }
      if (opts.length < 2) {
        toast.warning("Add at least two options.");
        return;
      }
      this.submitLoading = true;
      const data = new FormData();
      data.append("function", "createPoll");
      data.append("title", this.form.title.trim());
      data.append("description", this.form.description.trim());
      data.append("allowMultiple", this.form.allowMultiple ? 1 : 0);
      data.append("visibilityType", this.form.visibilityType);
      data.append("startDate", this.form.startDate.replace("T", " ") + ":00");
      data.append("endDate", this.form.endDate.replace("T", " ") + ":00");
      data.append("showResultsBeforeClose", this.form.showResultsBeforeClose ? 1 : 0);
      data.append("sessionIdNo", sessionStorage.getItem("idNo") || "");
      data.append("sessionNames", sessionStorage.getItem("names") || "");
      data.append("creatorDepartmentId", sessionStorage.getItem("departmentID") || 0);
      if (this.form.visibilityType === "department" && this.form.departmentIds.length) {
        data.append("departmentIds", JSON.stringify(this.form.departmentIds));
      }
      data.append("options", JSON.stringify(opts));
      execute(data, authUrl)
        .then((res) => {
          this.submitLoading = false;
          const body = res && res.data;
          const success = body && (body.success === true || body.success === "true");
          const message = (body && (body.message || (body.data && body.data.message))) || "Poll created.";
          if (success) {
            toast.success(message);
            if (this.$refs.pollModal && typeof this.$refs.pollModal.close === "function") {
              this.$refs.pollModal.close();
            }
            this.loadPolls();
          } else {
            toast.error(typeof body === "string" ? body : (message || "Failed to create poll."));
          }
        })
        .catch((err) => {
          this.submitLoading = false;
          const data = err && err.response && err.response.data;
          const msg = (data && typeof data === "object" && data.message)
            || (data && typeof data === "string" ? data : null)
            || (err && err.message)
            || "Request failed. Check that polls tables exist and try again.";
          toast.error(msg);
        });
    },
    viewResults(p) {
      const data = new FormData();
      data.append("function", "getPollResults");
      data.append("pollId", p.id);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data) {
            this.resultsTitle = res.data.data.title;
            this.resultsOptions = res.data.data.options || [];
            this.resultsUniqueVoters = res.data.data.uniqueVoters || 0;
            this.$refs.resultsModal?.open();
          } else {
            toast.error(res.data.message || "Could not load results.");
          }
        })
        .catch(() => toast.error("Request failed."));
    },
    updatePollAction(p, action) {
      const data = new FormData();
      data.append("function", "updatePoll");
      data.append("pollId", p.id);
      data.append("action", action);
      data.append("sessionIdNo", sessionStorage.getItem("idNo") || "");
      data.append("sessionNames", sessionStorage.getItem("names") || "");
      execute(data, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            toast.success(res.data.message || "Updated.");
            this.loadPolls();
          } else {
            toast.error(res?.data?.message || "Failed.");
          }
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message || "Request failed."));
    },
    deletePollAction(p) {
      if (!confirm("Delete this poll? This cannot be undone.")) return;
      const data = new FormData();
      data.append("function", "deletePoll");
      data.append("pollId", p.id);
      data.append("sessionIdNo", sessionStorage.getItem("idNo") || "");
      data.append("sessionNames", sessionStorage.getItem("names") || "");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            toast.success(res.data.message || "Deleted.");
            this.loadPolls();
          } else toast.error(res.data.message || "Failed.");
        })
        .catch(() => toast.error("Request failed."));
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.polls-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.poll-check-label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.hr-checkbox { width: 18px; height: 18px; }
.poll-dept-list { display: flex; flex-wrap: wrap; gap: 12px; }
.poll-dept-item { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.poll-opt-row { display: flex; gap: 8px; margin-bottom: 8px; }
.poll-opt-row .hr-input { flex: 1; }
.poll-progress-bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.poll-progress-fill { height: 100%; background: linear-gradient(90deg, #5b73e8, #7c8ff0); }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; }
.hr-text-end { text-align: right; }
.hr-text-truncate { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hr-d-block { display: block; }
.hr-btn-outline { background: transparent; border: 1px solid #e2e8f0; }
</style>
